import {
  Button,
  Drawer,
  Tooltip,
  Modal,
  Card,
  Tag,
  Space,
  Divider,
} from "antd";
import useClipboard from "../utils";
import { recordApi, RTMP_SERVER, studioApi } from "../api";
import { IVideo } from "../interfaces";
import { useState } from "react";
import useCourseStore from "../store/course";
import {
  CopyOutlined,
  DeleteOutlined,
  RedoOutlined,
  LinkOutlined,
  VideoCameraOutlined,
  CalendarOutlined,
  BookOutlined,
  CloudOutlined,
  GlobalOutlined,
  FileOutlined,
  EyeOutlined,
  BarChartOutlined,
  PlayCircleFilled,
} from "@ant-design/icons";
import moment from "moment";
import { message } from "antd";
import VideoProgress from "./videoProgress";
import { statusColor } from "../utils/constants";
import { useNavigate } from "@tanstack/react-router";
// import VideoRenderingProgress from "../screens/videoRenderingProgress";

interface IProps {
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  video: IVideo;
  onDelete: (video: IVideo) => void;
}

function VideoDetailDrawer({
  drawerOpen,
  setDrawerOpen,
  video,
  onDelete = (video) => {},
}: IProps) {
  const { getCourse } = useCourseStore();
  const { copy } = useClipboard();
  const [course] = useState(getCourse(video?.course_id));
  const [, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleDelete = async () => {
    await studioApi.delete(
      `videos/${video._id}?stream_key=${video.stream_key}`
    );
    onDelete(video);
    setDrawerOpen(false);
  };

  const handleTrash = async () => {
    try {
      const { data, status } = await studioApi.patch(
        `/videos/${video._id}/trashed`
      );

      if (status === 200) {
        message.success(data.message);
      }
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  // const handleContinue = async () => {
  //   try {
  //     await api.get(`resume-task`, {
  //       params: {
  //         streamKey: video.stream_key,
  //       },
  //     });
  //     message.success("Successfully Continued", 5);
  //   } catch (error) {
  //     message.error("Error While Continuing", 10);
  //   }
  // };

  const handleReload = async () => {
    if (["live", "schedule"].includes(video.status)) {
      return message.error("You Can't Restart as video is not ended");
    }
    try {
      await recordApi.post(`force-start/${video.stream_key}`);
      message.success("Successfully Restarted.");
    } catch (e) {
      message.error(e.message || "Internal server error");
    }
  };

  // const handleResume = async () => {
  //   if (["live", "schedule"].includes(video.status)) {
  //     return message.error("You Can't Resume as video is not ended");
  //   }
  //   try {
  //     // await recordApi.post(`/resume/${video._id}`, {});
  //     message.success("Successfully Resumed.", 5);
  //   } catch (e) {
  //     message.error("Internal server error", e);
  //   }
  // };

  const isLive = ["live", "rendering"].includes(video.status);

  const videoUrl = isLive
    ? `${RTMP_SERVER}/hls/${video.stream_key}.m3u8`
    : video.url.includes(".m3u8")
    ? video.url
    : `${video.url}/${video.stream_key}.m3u8`;

  const cdnUrl = isLive
    ? `${localStorage.getItem("live_CDN")}/hls/${video.stream_key}.m3u8`
    : video.cdnUrl.includes(".m3u8")
    ? video.cdnUrl
    : `${video.cdnUrl}/${video.stream_key}.m3u8`;

  // const mp4Url = video.downloadUrl
  //   ? `${video.downloadUrl}`
  //   : null;

  const InfoCard = ({
    icon,
    label,
    value,
    copyValue,
    isLink = false,
  }: {
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
    copyValue?: string;
    isLink?: boolean;
  }) => (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "12px 0",
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#0ea5e9",
          fontSize: 16,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 12,
            color: "#6b7280",
            marginBottom: 4,
            fontWeight: 500,
          }}
        >
          {label}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {isLink && typeof value === "string" ? (
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 14,
                color: "#0369a1",
                wordBreak: "break-all",
                textDecoration: "none",
              }}
            >
              {value}
            </a>
          ) : (
            <span
              style={{ fontSize: 14, color: "#1f2937", wordBreak: "break-all" }}
            >
              {value}
            </span>
          )}
          {copyValue && (
            <Tooltip title="Copy to clipboard">
              <Button
                type="text"
                size="small"
                onClick={() => copy(copyValue, `${label} copied`)}
                icon={<CopyOutlined style={{ color: "#0ea5e9" }} />}
                style={{ padding: 0, width: 28, height: 28 }}
              />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
  return (
    <Drawer
      title={
        <Space align="center">
          <span style={{ fontSize: 18, fontWeight: 600, color: "#1f2937" }}>
            Video #{video.vid}
          </span>
          <Tag color={statusColor[video.status]}>{video.status}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Button
            onClick={() => {
              Modal.warning({
                title: "Warning",
                content: "Are You Sure You Want to Restart Forcefully?",
                okText: "Yes",
                cancelText: "No",
                onOk: handleReload,
                onCancel: handleCancel,
              });
            }}
            icon={<RedoOutlined />}
            type="default"
          >
            Force Restart
          </Button>
          {/* <Button
            onClick={() => {
              Modal.confirm({
                title: "Confirm",
                content: "Are You Sure You Want to Delete?",
                okText: "Yes",
                cancelText: "No",
                onOk: handleDelete,
                onCancel: handleCancel,
              });
            }}
            danger
            icon={<DeleteOutlined />}
          >
            Delete
          </Button> */}

          <Button
            onClick={() => {
              Modal.confirm({
                title: "Confirm",
                content: "Are You Sure You Want to Trash?",
                okText: "Yes",
                cancelText: "No",
                onOk: handleTrash,
                onCancel: handleCancel,
              });
            }}
            danger
            icon={<DeleteOutlined />}
          >
            Trash
          </Button>
        </Space>
      }
      placement="right"
      width={600}
      onClose={() => setDrawerOpen(false)}
      open={drawerOpen}
      bodyStyle={{ padding: "20px 24px" }}
      headerStyle={{ borderBottom: "1px solid #f3f4f6" }}
    >
      {/* Title Section */}
      <Card
        style={{
          marginBottom: 20,
          borderRadius: 12,
          border: "1px solid #e5e7eb",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
        bodyStyle={{ padding: 20 }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: 20,
            }}
          >
            <VideoCameraOutlined />
          </div>
          <div style={{ flex: 1 }}>
            <h3
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 600,
                color: "#1f2937",
                lineHeight: 1.4,
              }}
            >
              {video.title}
            </h3>
            <span style={{ fontSize: 12, color: "#6b7280" }}>
              Stream Key: {video.stream_key}
            </span>
          </div>
        </div>
      </Card>

      {/* Video Details */}
      <Card
        title={
          <Space>
            <BarChartOutlined style={{ color: "#0ea5e9" }} />
            <span style={{ fontWeight: 600, color: "#374151" }}>
              Video Details
            </span>
          </Space>
        }
        style={{
          marginBottom: 20,
          borderRadius: 12,
          border: "1px solid #e5e7eb",
        }}
        headStyle={{ borderBottom: "1px solid #f3f4f6", padding: "16px 20px" }}
        bodyStyle={{ padding: "0 20px" }}
      >
        <InfoCard
          icon={<CalendarOutlined />}
          label="Scheduled For"
          value={moment(video.schedule_at).format("DD MMM YYYY, HH:mm")}
        />
        <Divider style={{ margin: 0 }} />
        {course && (
          <>
            <InfoCard
              icon={<BookOutlined />}
              label="Course"
              value={course.title}
            />
            <Divider style={{ margin: 0 }} />
          </>
        )}
        <InfoCard
          icon={<CloudOutlined />}
          label="Bucket"
          value={video.bucket}
        />
      </Card>

      {/* URLs Section */}
      <Card
        title={
          <Space>
            <LinkOutlined style={{ color: "#0ea5e9" }} />
            <span style={{ fontWeight: 600, color: "#374151" }}>
              Video URLs
            </span>

            <Tooltip title="Play" arrow={false}>
              <PlayCircleFilled
                style={{
                  color: "#0ea5e9",
                  fontSize: "1.5em",
                  cursor: "pointer",
                }}
                onClick={() =>
                  window.open(
                    `https://khanglobalstudies.com/player?src=${cdnUrl}`,
                    "_blank"
                  )
                }
              />
            </Tooltip>
            <Button
              size="small"
              onClick={() =>
                navigate({
                  to: "/bulk-api",
                  search: {
                    stream_key: video?.stream_key,
                  },
                })
              }
            >
              View Logs
            </Button>
          </Space>
        }
        style={{
          marginBottom: 20,
          borderRadius: 12,
          border: "1px solid #e5e7eb",
        }}
        headStyle={{ borderBottom: "1px solid #f3f4f6", padding: "16px 20px" }}
        bodyStyle={{ padding: "0 20px" }}
      >
        {video.status === "schedule" ? (
          <div
            style={{ padding: "20px 0", textAlign: "center", color: "#6b7280" }}
          >
            URLs will be available once the video goes live
          </div>
        ) : (
          <>
            <InfoCard
              icon={<GlobalOutlined />}
              label="Video URL (HLS)"
              value={videoUrl}
              copyValue={videoUrl}
            />
            <Divider style={{ margin: 0 }} />
            <InfoCard
              icon={<CloudOutlined />}
              label="CDN URL (HLS)"
              value={cdnUrl}
              copyValue={cdnUrl}
            />
            {video.downloadUrl && (
              <>
                <Divider style={{ margin: 0 }} />
                <InfoCard
                  icon={<FileOutlined />}
                  label="MP4 Download URL"
                  value={video.downloadUrl}
                  copyValue={video.downloadUrl}
                  isLink
                />
              </>
            )}
          </>
        )}
      </Card>

      {video?.videoType !== "youtube" && (
        <Card
          title={
            <Space>
              <EyeOutlined style={{ color: "#0ea5e9" }} />
              <span style={{ fontWeight: 600, color: "#374151" }}>
                Rendering Status
              </span>
              {video.log && (
                <a
                  href={`logs?stream-key=${video.stream_key}`}
                  style={{ fontSize: 12, marginLeft: 8, color: "#0ea5e9" }}
                >
                  View Logs →
                </a>
              )}
            </Space>
          }
          style={{
            borderRadius: 12,
            border: "1px solid #e5e7eb",
          }}
          headStyle={{
            borderBottom: "1px solid #f3f4f6",
            padding: "16px 20px",
          }}
          // bodyStyle={{ padding: 20 }}
        >
          <VideoProgress video={video} stream_key={video?.stream_key} />
        </Card>
      )}
    </Drawer>
  );
}

export default VideoDetailDrawer;
